/**
 * Calculates the Weibull Probability Density Function (PDF).
 * @param x The value to evaluate (x >= 0).
 * @param k The shape parameter (k > 0).
 * @param lambda The scale parameter (λ > 0).
 * @returns The value of the PDF at x.
 */
const weibullPDF = (x: number, k: number, lambda: number): number => {
  // Weibull is defined for x >= 0, k > 0, and lambda > 0.
  if (x < 0 || k <= 0 || lambda <= 0) {
    return 0
  }

  const term1 = k / lambda
  const term2 = Math.pow(x / lambda, k - 1)
  const term3 = Math.exp(-Math.pow(x / lambda, k))

  return term1 * term2 * term3
}

/**
 * Generates data points for the Weibull distribution curve.
 * @param c The shape parameter.
 * @param scale The scale parameter.
 * @param loc The loc parameter.
 * @param lbound - Lower boundary for data.
 * @param ubound - Upper boundary for data.
 * @param numPoints The number of points to generate.
 * @returns An object with x-values (labels) and y-values (data).
 */
export const weibullSample = (
  c: number,
  scale: number,
  loc: number,
  lbound?: number,
  ubound?: number,
  numPoints: number = 100,
): { labels: number[]; data: number[] } => {
  const labels: number[] = []
  const data: number[] = []
  const step = 0.1
  for (let i = 0; i <= numPoints; i++) {
    const x = i * step
    const pdf = weibullPDF(x, c, scale)
    const y = x + loc
    if (lbound && x < lbound) continue
    if (ubound && x > ubound) continue
    labels.push(Math.round(y * 100) / 100)
    data.push(pdf)
  }
  return { labels, data }
}
